import { AlarmSignalR } from 'domain/alarm-signal-r'
import { useSignalR } from 'layout/context/signalr-context'
import { Badge } from 'primereact/badge'
import { Button } from 'primereact/button'
import { OverlayPanel } from 'primereact/overlaypanel'
import { Fragment, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import MercosulPlate from 'shared/mercosul-plate/mercosul-license-plate'
import { AnprCriterionTypeEnum } from '../types/alarm.types'
import {
  getAlarmTypeBadgeClass,
  getAlarmTypeIcon,
  getAlarmTypeLabel,
} from '../types/signal-r-alarm-type.helpers'
import {
  formatRelativeAlarmTime,
  getCriterionTypeLabel,
  renderCriterionValue,
} from '../utils/alarm-signal-r-display'
import './alarm-feed-bell.scss'

/**
 * Sino de alarmes no topbar: mostra os alarmes recebidos nesta sessão
 * (SignalRContext.alarmHistory, até 50) sem precisar abrir a tela de
 * Alarmes. O AlarmDialog já cobre o alarme mais recente com um modal de
 * atenção total; este painel é para os que já passaram e ninguém olhou.
 */
const AlarmFeedBell = () => {
  const { alarmHistory, unreadAlarmCount, markAlarmsAsRead } = useSignalR()
  const overlayRef = useRef<OverlayPanel>(null)
  const navigate = useNavigate()
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)

  const handleToggle = (event: React.SyntheticEvent) => {
    overlayRef.current?.toggle(event)
    markAlarmsAsRead()
  }

  const goToHistory = () => {
    overlayRef.current?.hide()
    navigate('/alarms')
  }

  const plateOf = (alarm: AlarmSignalR) =>
    alarm.criterias?.find(
      (criteria) => criteria.type === AnprCriterionTypeEnum.LICENSE_PLATE
    )

  return (
    <div className='action-item alarm-feed-bell'>
      <Button
        icon='pi pi-bell'
        className='topbar-action-button'
        onClick={handleToggle}
        tooltip='Alarmes recentes'
        tooltipOptions={{ position: 'bottom' }}
        aria-label='Alarmes recentes'
      />
      {unreadAlarmCount > 0 && (
        <Badge
          value={unreadAlarmCount > 9 ? '9+' : unreadAlarmCount}
          severity='danger'
          className='alarm-feed-bell__badge'
        />
      )}

      <OverlayPanel
        ref={overlayRef}
        className='alarm-feed-panel'
        showCloseIcon
      >
        <div className='alarm-feed-panel__header'>
          <i className='pi pi-bell' />
          <span>Alarmes desta sessão</span>
        </div>

        {alarmHistory.length === 0 ? (
          <div className='alarm-feed-panel__empty'>
            <i className='pi pi-check-circle' />
            <span>Nenhum alarme recebido desde que você abriu o app</span>
          </div>
        ) : (
          <ul className='alarm-feed-panel__list'>
            {alarmHistory.map((alarm, index) => {
              const isExpanded = expandedIndex === index
              const plate = plateOf(alarm)
              const otherCriterias = (alarm.criterias ?? []).filter(
                (criteria) => criteria !== plate
              )

              return (
                <li
                  key={`${alarm.alarmDate}-${index}`}
                  className={`alarm-feed-panel__item${isExpanded ? ' alarm-feed-panel__item--expanded' : ''}`}
                >
                  <button
                    type='button'
                    className='alarm-feed-panel__item-header'
                    onClick={() =>
                      setExpandedIndex(isExpanded ? null : index)
                    }
                  >
                    <span
                      className={`alarm-feed-panel__icon ${getAlarmTypeBadgeClass(alarm.type)}`}
                    >
                      <i className={`pi ${getAlarmTypeIcon(alarm.type)}`} />
                    </span>
                    <span className='alarm-feed-panel__info'>
                      <span className='alarm-feed-panel__type'>
                        {getAlarmTypeLabel(alarm.type)}
                      </span>
                      <span className='alarm-feed-panel__camera'>
                        {alarm.cameraName || 'Câmera desconhecida'}
                      </span>
                    </span>
                    <span className='alarm-feed-panel__time'>
                      {formatRelativeAlarmTime(alarm.alarmDate)}
                    </span>
                  </button>

                  {isExpanded && (
                    <div className='alarm-feed-panel__details'>
                      {alarm.alarmImageUrl && (
                        <img
                          className='alarm-feed-panel__image'
                          src={alarm.alarmImageUrl}
                          alt={`Evidência do alarme ${getAlarmTypeLabel(alarm.type)}`}
                        />
                      )}

                      {plate && (
                        <MercosulPlate
                          licensePlate={plate.value}
                          size='S'
                        />
                      )}

                      {otherCriterias.length > 0 && (
                        <dl className='alarm-feed-panel__criteria'>
                          {otherCriterias.map((criteria, criteriaIndex) => (
                            <Fragment key={`${criteria.type}-${criteriaIndex}`}>
                              <dt>
                                {getCriterionTypeLabel(criteria.type, false)}
                              </dt>
                              <dd>{renderCriterionValue(criteria)}</dd>
                            </Fragment>
                          ))}
                        </dl>
                      )}
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        )}

        <div className='alarm-feed-panel__footer'>
          <Button
            label='Ver histórico completo'
            icon='pi pi-history'
            text
            onClick={goToHistory}
          />
        </div>
      </OverlayPanel>
    </div>
  )
}

export default AlarmFeedBell
